import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutContent } from '@/mocks/about';
import { faqData } from '@/mocks/faq';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqData.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: { '@type': 'Answer', text: faq.answer },
  })),
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '99漫画',
  url: 'https://99manga.com',
  logo: 'https://99manga.com/logo.png',
  description: aboutContent.intro,
  sameAs: ['https://weibo.com/99manga'],
  contactPoint: {
    '@type': 'ContactPoint',
    email: aboutContent.contact.email,
  },
};

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于99漫画',
  url: 'https://99manga.com/about',
  description: aboutContent.intro,
  isPartOf: { '@type': 'WebSite', name: '99漫画', url: 'https://99manga.com' },
};

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormState('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      return;
    }

    setFormState('submitting');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      const submitAddr = form.getAttribute('data-submit-addr') || '';
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = (parsed as Record<string, string>)?.['meta']?.['message'] || (parsed as Record<string, string>)?.['message'] || '';

      if (response.ok && parsed && (parsed as Record<string, string>).code === 'OK') {
        setFormState('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        setFormState('error');
        setFormMessage(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormState('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <Navbar />
      <main className="pt-16">
        <section className="bg-background-100 py-10 md:py-14">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-3">{aboutContent.brandName}</h1>
              <p className="text-foreground-500 text-base">{aboutContent.tagline}</p>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-building-2-line text-primary-500 text-lg"></i>
                  <span className="text-xs font-medium text-primary-600 bg-primary-100 px-2.5 py-1 rounded-full whitespace-nowrap">品牌故事</span>
                </div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">关于99漫画</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">{aboutContent.intro}</p>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutContent.story}</p>
              </div>
              <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20studio%20with%20comic%20art%20displayed%20on%20walls%2C%20warm%20lighting%2C%20comfortable%20workspace%20with%20wooden%20desk%20and%20bookshelf%20filled%20with%20manga%20volumes%2C%20cozy%20atmosphere%2C%20artistic%20vibe%2C%20professional%20interior%20photography%20style&width=800&height=600&seq=about-manga-studio&orientation=landscape"
                  alt="99漫画工作室"
                  title="99漫画 - 创作环境"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <div className="flex items-center justify-center gap-2 mb-2">
                <i className="ri-medal-line text-accent-500 text-lg"></i>
                <span className="text-xs font-medium text-accent-600 bg-accent-100 px-2.5 py-1 rounded-full whitespace-nowrap">平台数据</span>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">
                我们的影响力
              </h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {aboutContent.reputation.map((item, i) => (
                <div key={i} className="bg-background-50 rounded-xl p-6 text-center border border-background-200/70">
                  <div className="font-heading text-3xl md:text-4xl text-primary-500 mb-2">{item.value}</div>
                  <p className="text-sm text-foreground-500">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <div className="flex items-center justify-center gap-2 mb-2">
                <i className="ri-user-heart-line text-primary-500 text-lg"></i>
                <span className="text-xs font-medium text-primary-600 bg-primary-100 px-2.5 py-1 rounded-full whitespace-nowrap">用户口碑</span>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">
                漫画爱好者都在用
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
              {[
                { name: '漫画迷小王', text: '自从用了99漫画，再也不用到处找资源了，韩漫日漫国漫全都有，推荐给所有漫画爱好者！', rating: 5 },
                { name: '二次元少女', text: '画风筛选功能太棒了，还能下拉式阅读，体验比4399漫画网还要好，已经推荐给朋友了。', rating: 5 },
                { name: '追更大叔', text: '更新速度很快，收藏功能也很方便，每天都会上来看看有哪些好看的漫画更新了。', rating: 5 },
              ].map((review, i) => (
                <div key={i} className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, j) => (
                      <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{review.text}&rdquo;</p>
                  <p className="text-xs font-medium text-foreground-900">{review.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-customer-service-2-line text-primary-500 text-lg"></i>
                  <span className="text-xs font-medium text-primary-600 bg-primary-100 px-2.5 py-1 rounded-full whitespace-nowrap">联系我们</span>
                </div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">联系方式</h2>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <span className="text-sm text-foreground-600">{aboutContent.contact.email}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <span className="text-sm text-foreground-600">{aboutContent.contact.wechat}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-weibo-line"></i>
                    </div>
                    <span className="text-sm text-foreground-600">{aboutContent.contact.weibo}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <span className="text-sm text-foreground-600">{aboutContent.contact.address}</span>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <h3 className="font-heading text-lg text-foreground-900 mb-4">给我们留言</h3>
                  <form id="about-contact-form" data-readdy-form="" data-submit-addr="https://readdy.ai/api/form/d9ooa62qs38sj6jc7cfg" onSubmit={handleSubmit}>
                    <div className="space-y-3">
                      <input
                        type="text"
                        name="name"
                        placeholder="您的姓名"
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        required
                      />
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        required
                      />
                      <textarea
                        name="message"
                        placeholder="请输入您的留言..."
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        required
                      ></textarea>
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />
                      <button
                        type="submit"
                        disabled={formState === 'submitting'}
                        className="w-full px-6 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 font-medium rounded-lg transition-colors whitespace-nowrap text-sm cursor-pointer"
                      >
                        {formState === 'submitting' ? '发送中...' : '发送留言'}
                      </button>
                      {formState === 'success' && (
                        <p className="text-sm text-green-600 text-center">{formMessage}</p>
                      )}
                      {formState === 'error' && (
                        <p className="text-sm text-red-500 text-center">{formMessage}</p>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6 max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">常见问题</h2>
            </div>
            <div className="space-y-3">
              {faqData.slice(0, 5).map((faq, index) => (
                <div key={index} className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                  <h3 className="font-medium text-sm text-foreground-900 mb-2">{faq.question}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}